import React from 'react';
import { t } from '../i18n';
import { Textarea } from './ui';

/**
 * The questions of an assessment form, for answering (migrations 0065, 0068):
 * by the patient from Inicio, or by the professional during a session.
 * Controlled: the answers live in the caller, lined up with the questions by
 * position.
 *
 * A scale shows each value with its label; a choice, its options in the order
 * they were written, answered by position so the printed order is kept
 * whatever each option scores; yes/no, two choices; a text question, a box that
 * may stay empty.
 */

const Option = ({ name, chosen, onChoose, children }) => (
  <label
    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
      chosen ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
    }`}
  >
    <input type="radio" name={name} checked={chosen} onChange={onChoose} />
    {children}
  </label>
);

const FormAnswer = ({ questions, answers, onChange, idPrefix = 'form' }) => {
  const set = (index, value) => {
    const next = [...answers];
    next[index] = value;
    onChange(next);
  };

  return (
    <ol className="space-y-5">
      {questions.map((question, index) => {
        const name = `${idPrefix}-q${index}`;
        return (
          // Questions have no id of their own: their position is what answers
          // line up with, and a form's questions never reorder while answering.
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className="space-y-2">
            <p className="text-sm font-medium text-slate-900">
              {index + 1}.{question.prompt ? ` ${question.prompt}` : ''}
            </p>

            {question.kind === 'scale' && (
              <div className="flex flex-wrap gap-2" role="radiogroup">
                {(question.labels ?? []).map((label, offset) => {
                  const value = Number(question.min) + offset;
                  return (
                    <Option key={value} name={name} chosen={answers[index] === value} onChoose={() => set(index, value)}>
                      {label || value}
                    </Option>
                  );
                })}
              </div>
            )}

            {question.kind === 'choice' && (
              <div className="flex flex-col gap-2" role="radiogroup">
                {(question.options ?? []).map((option, position) => (
                  // The option's position is the answer, so two options may
                  // share a score and the order stays as written.
                  // eslint-disable-next-line react/no-array-index-key
                  <Option
                    key={position}
                    name={name}
                    chosen={answers[index] === position}
                    onChoose={() => set(index, position)}
                  >
                    {option.label}
                  </Option>
                ))}
              </div>
            )}

            {question.kind === 'yes_no' && (
              <div className="flex flex-wrap gap-2" role="radiogroup">
                <Option name={name} chosen={answers[index] === true} onChoose={() => set(index, true)}>
                  {t('common.yes')}
                </Option>
                <Option name={name} chosen={answers[index] === false} onChoose={() => set(index, false)}>
                  {t('common.no')}
                </Option>
              </div>
            )}

            {question.kind === 'text' && (
              <Textarea
                rows={3}
                value={answers[index] ?? ''}
                onChange={(event) => set(index, event.target.value || null)}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default FormAnswer;
